import { Combobox } from '@/components/Combobox'
import { useToast } from '@/hooks/useToast'
import { useUpdateAutoSync } from '@/features/inbox/queries'
import {
  SYNC_INTERVAL_MINUTES,
  intervalToPreset,
  type InboxAccount,
  type SyncInterval,
} from '@/features/inbox/types'

const OPTIONS: { value: SyncInterval; label: string }[] = [
  { value: 'manual', label: 'Manual only' },
  { value: '15min', label: 'Every 15 minutes' },
  { value: 'hourly', label: 'Every hour' },
  { value: '6h', label: 'Every 6 hours' },
  { value: 'daily', label: 'Every day' },
]

export function SyncIntervalControl({ account }: { account: InboxAccount }) {
  const { toast } = useToast()
  const updateAutoSync = useUpdateAutoSync()
  const current = intervalToPreset(account.sync_interval_minutes, account.auto_sync_enabled)

  const onChange = async (next: SyncInterval | null) => {
    if (!next || next === current) return
    const enabled = next !== 'manual'
    const minutes = enabled ? SYNC_INTERVAL_MINUTES[next] : account.sync_interval_minutes || 1440
    try {
      await updateAutoSync.mutateAsync({
        id: account.id,
        sync_interval_minutes: minutes,
        auto_sync_enabled: enabled,
      })
      toast({
        title: 'Auto-sync updated',
        description: OPTIONS.find((o) => o.value === next)?.label,
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Update failed'
      toast({ title: 'Could not update auto-sync', description: msg, variant: 'destructive' })
    }
  }

  return (
    <Combobox<SyncInterval>
      value={current}
      onChange={onChange}
      options={OPTIONS}
      disabled={updateAutoSync.isPending}
      triggerClassName="w-full sm:w-56"
    />
  )
}
